/**
 * The Reporting page's progress arithmetic — §A2, §A3 and §A4 of `05-reporting-marking.md`.
 *
 * Three things live here, and they live here for one reason: `04-officials-profiles.md` §A3
 * says of the report-status map that *"in the build it must be one shared derived value, not
 * two copies"*, and §A3 of the reporting spec says of the stat boxes that they are *"computed
 * live from the roster and the report-stage map — they cannot drift from the pills below
 * them"*. A percentage computed on the server and a pill tone chosen on the client are
 * exactly the two copies those sentences forbid, so both are derived from this module.
 *
 *  1. {@link REPORT_PROGRESS_BOXES} — the three boxes, their verbatim copy and, crucially,
 *     the **stage threshold each one counts**.
 *  2. {@link reportProgressPercent} and {@link reportProgressColour} — the figure and the
 *     maroon-to-green interpolation of §A3's colour rule.
 *  3. {@link reportStagePillTone} — the tone of the five-stage `Report Status` pill.
 *
 * **Counting is by stage and by nothing else (ruling D-4, `rulings-2026-08-11-open-questions.md`
 * §G).** §A3 defines `Reports Drafted` as *"officials whose TD Draft Report holds text"* and
 * D-4 makes that definition true at write time: a report may not sit on `AI Drafted` or `TD
 * Drafted` while it holds no narrative. The stage therefore already means "the draft holds
 * text", and a second text check in the counting query would be a rule enforced in two places
 * that could disagree. There is no such check here, deliberately.
 *
 * British English throughout, per the standing copy rule.
 */
/** §A3's three progress stat boxes, in the order the `.statgrid` renders them. */
export const REPORT_PROGRESS_BOXES = [
    {
        key: "drafted",
        heading: "Reports Drafted",
        panelNote: "TD Draft Report Started",
        note: "Officials whose **TD Draft Report** holds text — whether or not it has been pushed.",
        minimumStage: 2,
    },
    {
        key: "submitted",
        heading: "Reports Submitted",
        panelNote: "Pushed To TMS As Draft",
        note: "Pushed or repushed from the profile page. Publishing stays a deliberate step " +
            "**inside TMS**.",
        minimumStage: 3,
    },
    {
        key: "published",
        heading: "TMS Published",
        panelNote: "Confirmed In TMS",
        note: "Flipped from draft to confirmed by TMS. Shown as a count and a share of the panel, " +
            "like the two boxes beside it.",
        minimumStage: 4,
    },
];
/**
 * The three counts, from the stages of everybody in the denominator.
 *
 * By stage, per §A3 and ruling D-4 — see the module note. The denominator is the length of
 * `stages`, so a caller that has already excluded the TD (ruling R2′) cannot then count him
 * in one of the numerators.
 */
export function countReportProgress(stages) {
    const counts = {
        drafted: 0,
        submitted: 0,
        published: 0,
    };
    for (const box of REPORT_PROGRESS_BOXES) {
        counts[box.key] = stages.filter((stage) => stage >= box.minimumStage).length;
    }
    return counts;
}
/**
 * The share of the panel, as §A3's `NN.N%` figure — one decimal, never rounded to a whole.
 *
 * A denominator of zero yields 0: there is no panel to be a share of, and the screen's own
 * empty state says so in words rather than leaving a `NaN%` on the page.
 */
export function reportProgressPercent(count, denominator) {
    if (denominator <= 0)
        return 0;
    return Math.round((count / denominator) * 1000) / 10;
}
/** `NN.N` — the figure without its sign, which §A3 puts in its own `.sign` span. */
export function formatReportProgressPercent(pct) {
    return pct.toFixed(1);
}
/** §A3's colour rule: maroon at 0%. */
export const REPORT_PROGRESS_COLOUR_MIN = { r: 128, g: 0, b: 0 };
/** §A3's colour rule: green at 100%. */
export const REPORT_PROGRESS_COLOUR_MAX = { r: 29, g: 122, b: 70 };
/**
 * The figure's and the bar's colour, interpolated **linearly on `pct/100`** between maroon
 * `rgb(128,0,0)` and green `rgb(29,122,70)` (§A3).
 *
 * Out-of-range input is clamped rather than extrapolated: a percentage is a percentage, and
 * a bug upstream must not paint a colour outside the two the spec names.
 */
export function reportProgressColour(pct) {
    const t = Math.min(1, Math.max(0, pct / 100));
    const channel = (from, to) => Math.round(from + (to - from) * t);
    const r = channel(REPORT_PROGRESS_COLOUR_MIN.r, REPORT_PROGRESS_COLOUR_MAX.r);
    const g = channel(REPORT_PROGRESS_COLOUR_MIN.g, REPORT_PROGRESS_COLOUR_MAX.g);
    const b = channel(REPORT_PROGRESS_COLOUR_MIN.b, REPORT_PROGRESS_COLOUR_MAX.b);
    return `rgb(${String(r)}, ${String(g)}, ${String(b)})`;
}
/**
 * The tone of one `Report Status` pill (§A4, `04-officials-profiles.md` §A3).
 *
 * Five stages need five distinguishable tones, and the pipeline reads left to right: nothing
 * written (muted), a machine draft (info), the TD's own draft (warn — it is his to push),
 * pushed as a draft to TMS (accent), confirmed and published (good). The **labels** and the
 * **tooltips** are not restated here: they live in `report-stages.ts` and are read from
 * there, so a rename cannot leave a pill saying one thing and a stage map another.
 */
export function reportStagePillTone(stage) {
    switch (stage) {
        case 0:
            return "muted";
        case 1:
            return "info";
        case 2:
            return "warn";
        case 3:
            return "accent";
        case 4:
            return "good";
    }
}
//# sourceMappingURL=report-progress.js.map